import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, UserIcon } from 'lucide-react';
export function CustomerForm() {
  const {
    t
  } = useTranslation('customers');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('id');
  const isEditMode = !!customerId;
  const [customerType, setCustomerType] = useState<'company' | 'individual'>('company');
  const [formData, setFormData] = useState({
    // Company
    companyName: isEditMode ? 'Acme Corporation' : '',
    contactPerson: '',
    taxId: '',
    // Individual
    fullName: '',
    // Shared
    email: isEditMode ? 'billing@acme.com' : '',
    phone: isEditMode ? '+66 12 345 6789' : '',
    addressLine1: isEditMode ? '123 Business Street' : '',
    addressLine2: '',
    city: isEditMode ? 'Kuala Lumpur' : '',
    province: '',
    country: 'Malaysia',
    postcode: '',
    internalNotes: '',
    status: 'active' as 'active' | 'inactive'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving customer:', formData);
    navigate('/customers');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  return <div className="max-w-3xl mx-auto space-y-6 pb-12" data-id="element-5418" data-mp="1ytkq9d0evgfb">
      {/* Header */}
      <div className="flex items-center space-x-4" data-id="element-5419" data-mp="1yrgi2ivhbv98">
        <button onClick={() => navigate('/customers')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-5420" data-mp="1kt9txugc19mr">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-5421" data-mp="9dtlezeunnz9" />
          {t('pages.CustomerForm.back_to_customers')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5422" data-mp="dhsvp09jdqk6">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-5423" data-mp="2g2dfs458wuvj">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-5424" data-mp="2b3sfk4pzub73">
            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-10" data-id="element-5425" data-mp="26az9ndisgwtf">
          {/* SECTION 1: Customer Information */}
          <div className="space-y-6" data-id="element-5426" data-mp="1xcdvrvo9i5yz">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-5427" data-mp="1xdf7m2d3qwhr">
              {t('pages.CustomerForm.customer_information')}
            </h2>

            {/* Customer Type Toggle */}
            <div className="flex p-1 bg-gray-100 rounded-lg w-fit" data-id="element-5428" data-mp="1ndodi1sgeji7">
              <button type="button" onClick={() => setCustomerType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${customerType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5429" data-mp="1ifehjoywnevj">
                <BuildingIcon size={16} className="mr-2" data-id="element-5430" data-mp="3l9dcr5n3jb2f" />
                {t('pages.CustomerForm.company')}
              </button>
              <button type="button" onClick={() => setCustomerType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${customerType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5431" data-mp="3pa71g18d4twd">
                <UserIcon size={16} className="mr-2" data-id="element-5432" data-mp="36cm7ifqqr54w" />
                {t('pages.CustomerForm.individual')}
              </button>
            </div>

            {customerType === 'company' ? <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5433" data-mp="3gb1xughy2adc">
                <div data-id="element-5434" data-mp="3g8z0tcidjgz4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5435" data-mp="qm9rllu6ogv5">
                    {t('pages.CustomerForm.company_name')}
                  </label>
                  <input type="text" name="companyName" required value={formData.companyName} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.enter_company_name')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5436" data-mp="vn77tsvmv0u0" />
                </div>
                <div data-id="element-5437" data-mp="2igrc5pikc4ys">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5438" data-mp="2ihaqkw908b82">
                    {t('pages.CustomerForm.contact_person')}
                  </label>
                  <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.enter_contact_person')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5439" data-mp="2s8onqxdhgyy1" />
                </div>
                <div className="md:col-span-2" data-id="element-5440" data-mp="3avo456x2fmy2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5441" data-mp="3fuheiqh22xu0">
                    {t('pages.CustomerForm.tax_id_registration_number')}
                  </label>
                  <input type="text" name="taxId" value={formData.taxId} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.13digit_registration_number')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5442" data-mp="36u2iaw2jzybq" />
                </div>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5443" data-mp="toxhsffjfp0s">
                <div className="md:col-span-2" data-id="element-5444" data-mp="ovuu2nxfz9si">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5445" data-mp="ov33jripc293">
                    {t('pages.CustomerForm.full_name')}
                  </label>
                  <input type="text" name="fullName" required value={formData.fullName} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.enter_full_name')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5446" data-mp="f0b40i5k75bo" />
                </div>
              </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5447" data-mp="3jcy6322mjjb6">
              <div data-id="element-5448" data-mp="3jbn2p3d3cdkl">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5449" data-mp="3jfavg0apovy9">
                  {t('pages.CustomerForm.email_address')}
                </label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.billingcompanycom')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5450" data-mp="22x0qbr8elf33" />
              </div>
              <div data-id="element-5451" data-mp="2qn4vw455jowq">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5452" data-mp="2qmlagdgamidy">
                  {t('pages.CustomerForm.phone_number')}
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+66 12 345 6789" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5453" data-mp="w8u4pp134ins" />
              </div>
            </div>
          </div>

          {/* Divider: Billing information */}
          <div className="relative py-2" data-id="element-5454" data-mp="37v4cbnnk21rn">
            <div className="absolute inset-0 flex items-center" data-id="element-5455" data-mp="37w5z2pdxocp7">
              <div className="w-full border-t border-gray-200" data-id="element-5456" data-mp="2k6uwr3gxqa8y"></div>
            </div>
            <div className="relative flex justify-center" data-id="element-5457" data-mp="2g4zwm2aqq2wr">
              <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-5458" data-mp="2g6kr5rx21lum">
                {t('pages.CustomerForm.billing_information')}
              </span>
            </div>
          </div>

          {/* SECTION 2: Billing Information */}
          <div className="space-y-5" data-id="element-5459" data-mp="9c769k2onmhs">
            <div data-id="element-5460" data-mp="4enviu571rul">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5461" data-mp="4f70la9qwlr7">
                {t('pages.CustomerForm.address_line_1')}
              </label>
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.123_business_street')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5462" data-mp="1meeu1l99lmvu" />
            </div>
            <div data-id="element-5463" data-mp="2a24qwmieulzg">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5464" data-mp="2a0lbey4kweyq">
                {t('pages.CustomerForm.address_line_2')}
              </label>
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.suite_building_floor_etc')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5465" data-mp="219b9aifhraif" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5466" data-mp="1iozdkr36166y">
              <div data-id="element-5467" data-mp="1iqsahenz8c1r">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5468" data-mp="1mqav09bvyl42">
                  {t('pages.CustomerForm.city')}
                </label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.bangkok')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5469" data-mp="18xbga9jxguxk" />
              </div>
              <div data-id="element-5470" data-mp="uz51ibqo7lnr">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5471" data-mp="uzmtgw6avdpv">
                  {t('pages.CustomerForm.province')}
                </label>
                <input type="text" name="province" value={formData.province} onChange={handleChange} placeholder={t('pages.CustomerForm.province')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5472" data-mp="bffmz9hlcve4" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5473" data-mp="pc2xyg6dpm31">
              <div data-id="element-5474" data-mp="141qwxar388nl">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5475" data-mp="142a7g6eerurl">
                  {t('pages.CustomerForm.country')}
                </label>
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-5476" data-mp="z5a07s5tolyw">
                  {t('pages.CustomerForm.thailand')}
                </div>
              </div>
              <div data-id="element-5477" data-mp="5qz5i5layozd">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5478" data-mp="5rh8dl2h01g9">
                  {t('pages.CustomerForm.postcode')}
                </label>
                <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder={t('pages.CustomerForm.postcode')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-5479" data-mp="er4aya6xrdaw" />
              </div>
            </div>
          </div>

          {/* Divider: Additional information */}
          <div className="relative py-2" data-id="element-5480" data-mp="12ff6cxtv4krs">
            <div className="absolute inset-0 flex items-center" data-id="element-5481" data-mp="12ecf2osmogbc">
              <div className="w-full border-t border-gray-200" data-id="element-5482" data-mp="307jgcep20oqp"></div>
            </div>
            <div className="relative flex justify-center" data-id="element-5483" data-mp="348uy5y1os5oc">
              <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-5484" data-mp="2woakm3vtox7n">
                {t('pages.CustomerForm.additional_information')}
              </span>
            </div>
          </div>

          {/* SECTION 3: Additional Information */}
          <div className="space-y-5" data-id="element-5485" data-mp="2mwemistql73f">
            <div data-id="element-5486" data-mp="2ixfl3i77rsgq">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5487" data-mp="2ixxdu4uo3ojw">
                {t('pages.CustomerForm.internal_notes')}
              </label>
              <textarea name="internalNotes" value={formData.internalNotes} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.add_any_internal_notes_about_this')} rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors resize-none" data-id="element-5488" data-mp="3ao276t0enlx9" />
            </div>
            <div data-id="element-5489" data-mp="3e56chlo4hpf3">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5490" data-mp="3e4odiha2sx3h">
                {t('pages.CustomerDetail.status_2')}
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-5491" data-mp="3ixhh31tt5zdz">
                <option value="active" data-id="element-5492" data-mp="5pmpv5hyj590">{t('pages.CustomerForm.active')}</option>
                <option value="inactive" data-id="element-5493" data-mp="3nuq014ms67ga">
                  {t('pages.CustomerForm.inactive')}
                </option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-5494" data-mp="2cfu9oleruhjw">
            <button type="button" onClick={() => navigate('/customers')} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-5495" data-mp="2henpikhj6kn8">
              {t('pages.CustomerForm.cancel')}
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-5496" data-mp="2iuhzlwc3t3d7">
              {isEditMode ? 'Update customer' : 'Create customer'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}